import { DateTime } from 'luxon';
import {
  formatDateTimeShort,
  lightTheme,
  SystemProblem,
  SystemProblemCategory,
  SystemProblemSeverity,
  SystemProblemSource,
  SystemProblemStatus,
  SystemProblemType,
} from '@vgrubs/components';

/**
 * Names, colours and actions for system problems.
 *
 * Colours are read inside functions, never into a module-level constant: a
 * theme value pulled from the barrel at module top level is undefined in an
 * eagerly loaded file.
 */

export const SYSTEM_PROBLEM_TYPE_LABEL: Record<SystemProblemType, string> = {
  duplicateStoreConnection: 'Duplicate store connection',
  foodhubConnectionFailed: 'POS Hub connection failed',
  foodhubConnectionPending: 'POS Hub connection pending',
  menuPublishFailed: 'Menu publish failed',
  menuPublishStuck: 'Menu publish stuck',
  vDriveAutoAssignFailed: 'No vDrive fleet',
  kitchenhubNotMainAccount: 'KitchenHub: not the main account',
  kitchenhubStoreNotFound: 'KitchenHub store not found',
  kitchenhubProviderDisabled: 'KitchenHub: provider disabled',
  kitchenhubWorkingHoursFailed: 'KitchenHub: working hours rejected',
  vDriveAutoRechargeFailed: 'vDrive auto-recharge failed',
  posOrderRejected: 'POS rejected an order',
  vDriveBrandMissingOrder: 'Order without vDrive',
  tabletOffline: 'Tablet offline',
  providerOffline: 'Provider offline',
  printerError: 'Printer error',
  saasNotEligible: 'SaaS charged but not eligible',
  scheduledPauseFailed: 'Scheduled pause failed',
};

/** A type this build does not know yet still gets a readable name. */
export const problemTypeLabel = (type: string) =>
  SYSTEM_PROBLEM_TYPE_LABEL[type as SystemProblemType] ?? type;

/** The card's filter chips, in the order they are shown. */
export const SYSTEM_PROBLEM_CATEGORIES: Array<{
  value: SystemProblemCategory;
  label: string;
}> = [
  { value: 'providerOffline', label: 'Provider offline' },
  { value: 'tabletOffline', label: 'Tablet offline' },
  { value: 'printer', label: 'Printer' },
  { value: 'vDrive', label: 'vDrive' },
  { value: 'menu', label: 'Menu publish' },
  { value: 'connections', label: 'Connections' },
  { value: 'duplicateStore', label: 'Duplicate store' },
  { value: 'posRejected', label: 'POS rejected' },
  { value: 'saas', label: 'SaaS' },
];

export const SYSTEM_PROBLEM_SEVERITIES: Array<{
  value: SystemProblemSeverity;
  label: string;
}> = [
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
];

/** Red, yellow, blue - the doc's suggestion, in theme tokens. */
export const severityColor = (severity: SystemProblemSeverity) =>
  ({
    critical: lightTheme.primary,
    warning: lightTheme.yellow,
    info: lightTheme.blue,
  }[severity] ?? lightTheme.lightGray);

export const severityLabel = (severity: SystemProblemSeverity) =>
  SYSTEM_PROBLEM_SEVERITIES.find((s) => s.value === severity)?.label ??
  severity;

/** FoodHub is "POS Hub" everywhere a merchant or support person reads it. */
export const SOURCE_LABEL: Record<SystemProblemSource, string> = {
  foodhub: 'POS Hub',
  kitchenhub: 'KitchenHub',
};

/**
 * The three status views of the list. `all` sends no status at all - it is
 * the one place a resolved problem and an open one sit side by side.
 */
export type SystemProblemView = 'open' | 'resolved' | 'all';

export const SYSTEM_PROBLEM_VIEWS: Array<{
  value: SystemProblemView;
  label: string;
}> = [
  { value: 'all', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
];

export const viewStatus = (
  view: SystemProblemView
): SystemProblemStatus | null => (view === 'all' ? null : view);

/**
 * The one pill a problem wears. Open: how urgent, in its severity's colour.
 * Resolved: green, and whether the system closed it itself - "Resolved"
 * alone would read as somebody having done something.
 */
export const problemPill = (problem: SystemProblem) => {
  if (problem.status === 'resolved') {
    return {
      label: problem.autoResolved ? 'Resolved automatically' : 'Resolved',
      color: lightTheme.green,
    };
  }
  return {
    label: severityLabel(problem.severity),
    color: severityColor(problem.severity),
  };
};

/**
 * When a problem appeared, the way the card reads it: minutes and hours while
 * that is how support thinks about it, a date after a day.
 */
export const formatProblemAge = (iso: string) => {
  const minutes = Math.floor(
    DateTime.now().diff(DateTime.fromISO(iso)).as('minutes')
  );
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return formatDateTimeShort(iso);
};

/**
 * The brand a problem is about, or null when it has none worth showing: the
 * location's own brand (`isLocation`) is the location itself, so the problem
 * reads as the location's.
 */
export const problemBrandName = (problem: SystemProblem) =>
  problem.isLocation ? null : problem.brandName;

/**
 * Where a problem arose, which order, and who caused it, on one line:
 * "Queens · Burgerito · Order #4A2DA · by Ana". The brand is left out when it
 * is the location's own, or only repeats the location's name, which otherwise
 * reads as a glitch.
 */
export const problemWhereLabel = (problem: SystemProblem) => {
  const brandName = problemBrandName(problem);
  return [
    problem.locationName,
    brandName !== problem.locationName ? brandName : null,
    problem.orderNumber && `Order #${problem.orderNumber}`,
    problem.relatedUserName && `by ${problem.relatedUserName}`,
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * The one thing to do about a problem, as the button beside it on the card.
 * `kind` says where it goes; the component turns it into a route, or a phone
 * call for the two types that mean "ring the restaurant". `services` is the
 * location's services modal (SaaS package, total monthly fee, vTablet fees).
 */
export type ProblemActionKind =
  | 'order'
  | 'providers'
  | 'menu'
  | 'vDrive'
  | 'hours'
  | 'services'
  | 'call';

export const problemAction = (
  problem: SystemProblem
): { kind: ProblemActionKind; label: string } | null => {
  switch (problem.type) {
    case 'vDriveAutoAssignFailed':
      return { kind: 'order', label: 'Assign delivery' };
    case 'posOrderRejected':
    case 'vDriveBrandMissingOrder':
      return { kind: 'order', label: 'Open order' };
    case 'tabletOffline':
    case 'printerError':
      return { kind: 'call', label: 'Call restaurant' };
    case 'vDriveAutoRechargeFailed':
      return { kind: 'vDrive', label: 'Open vDrive' };
    case 'menuPublishFailed':
    case 'menuPublishStuck':
      return { kind: 'menu', label: 'Open menu' };
    case 'kitchenhubWorkingHoursFailed':
      return { kind: 'hours', label: 'Open working hours' };
    case 'saasNotEligible':
      return { kind: 'services', label: 'Open SaaS settings' };
    case 'duplicateStoreConnection':
    case 'foodhubConnectionFailed':
    case 'foodhubConnectionPending':
    case 'kitchenhubNotMainAccount':
    case 'kitchenhubStoreNotFound':
    case 'kitchenhubProviderDisabled':
    case 'providerOffline':
    case 'scheduledPauseFailed':
      return { kind: 'providers', label: 'Open providers' };
    default:
      return null;
  }
};
